class ProductDetails extends HTMLElement {
  connectedCallback() {
    this.variants = JSON.parse(this.querySelector('[data-product-variants]')?.textContent || '[]');
    this.optionInputs = [...this.querySelectorAll('[data-option-input]')];
    this.variantInput = this.querySelector('[data-product-variant-id]');
    this.mainAddButton = this.querySelector('[data-main-add-button]');
    this.productForm = this.querySelector('.product-form__form');
    this.dynamicCheckout = this.querySelector('[data-dynamic-checkout]');
    this.quantityInput = this.querySelector('[data-product-quantity-input]');
    this.formError = this.querySelector('[data-product-form-error]');
    this.mediaStage = this.querySelector('[data-product-media-stage]');
    this.stickyBar = this.querySelector('[data-sticky-product-bar]');

    this.optionInputs.forEach((input) => input.addEventListener('change', (event) => this.onOptionChange(event)));
    this.querySelectorAll('[data-media-thumbnail]').forEach((button) => {
      button.addEventListener('click', () => this.selectMedia(button.dataset.mediaTarget));
    });
    this.querySelectorAll('[data-quantity-action]').forEach((button) => {
      button.addEventListener('click', () => this.changeQuantity(button.dataset.quantityAction));
    });
    this.querySelector('[data-sticky-add]')?.addEventListener('click', () => {
      if (this.mainAddButton && !this.mainAddButton.disabled) this.productForm?.requestSubmit(this.mainAddButton);
    });
    this.productForm?.addEventListener('submit', (event) => this.onSubmit(event));
    this.mediaStage?.addEventListener('scroll', () => this.onMediaScroll(), { passive: true });

    this.setupStickyBar();
    this.updateOptionAvailability();
    if (this.dataset.initialMediaId) {
      window.requestAnimationFrame(() => this.selectMedia(this.dataset.initialMediaId, 'auto'));
    }
  }

  get selectedOptions() {
    return [...this.querySelectorAll('.product-form__option[data-option-position]')]
      .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
      .map((fieldset) => fieldset.querySelector('[data-option-input]:checked')?.value || '');
  }

  get currentVariant() {
    const selected = this.selectedOptions;
    if (selected.length === 0) {
      const selectedVariantId = Number(this.variantInput?.value);
      return this.variants.find((variant) => Number(variant.id) === selectedVariantId) || this.variants[0];
    }
    return this.variants.find((variant) => variant.options.every((option, index) => option === selected[index]));
  }

  onOptionChange(event) {
    let variant = this.currentVariant;
    const changedPosition = Number(event.target.dataset.optionPosition) - 1;

    if (!variant) {
      variant = this.variants.find((candidate) => candidate.options[changedPosition] === event.target.value && candidate.available)
        || this.variants.find((candidate) => candidate.options[changedPosition] === event.target.value);
      if (variant) this.selectVariantOptions(variant);
    }

    this.updateOptionAvailability();
    this.updateSelectedLabels();
    this.updateVariant(variant);
  }

  selectVariantOptions(variant) {
    variant.options.forEach((value, index) => {
      const input = this.querySelector(`[data-option-input][data-option-position="${index + 1}"][value="${CSS.escape(value)}"]`);
      if (input) input.checked = true;
    });
  }

  updateOptionAvailability() {
    const selected = this.selectedOptions;
    this.optionInputs.forEach((input) => {
      const position = Number(input.dataset.optionPosition) - 1;
      const prefix = selected.slice(0, position);
      const matchingVariants = this.variants.filter((variant) => {
        const prefixMatches = prefix.every((value, index) => variant.options[index] === value);
        return prefixMatches && variant.options[position] === input.value;
      });
      const exists = matchingVariants.length > 0;
      const available = matchingVariants.some((variant) => variant.available);
      input.disabled = !exists && !input.checked;
      input.nextElementSibling?.classList.toggle('is-unavailable', !available);
    });
  }

  updateSelectedLabels() {
    this.querySelectorAll('.product-form__option[data-option-position]').forEach((fieldset) => {
      const selected = fieldset.querySelector('[data-option-input]:checked');
      const label = fieldset.querySelector('[data-selected-option]');
      if (label) label.textContent = selected?.value || '';
    });
  }

  updateVariant(variant) {
    const price = this.querySelector('[data-product-price]');
    const compare = this.querySelector('[data-product-compare-price]');
    const availability = this.querySelector('[data-product-availability]');
    const sku = this.querySelector('[data-product-sku]');
    const stickyPrice = this.querySelector('[data-sticky-price]');
    const stickyButton = this.querySelector('[data-sticky-add]');

    if (!variant) {
      if (this.variantInput) this.variantInput.value = '';
      if (this.dynamicCheckout) this.dynamicCheckout.hidden = true;
      this.setPurchaseState(false, this.dataset.unavailableLabel);
      if (availability) {
        availability.dataset.available = 'false';
        availability.textContent = this.dataset.unavailableLabel;
      }
      return;
    }

    if (this.variantInput) {
      this.variantInput.value = variant.id;
      this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (price) price.textContent = variant.priceFormatted;
    if (stickyPrice) stickyPrice.textContent = variant.priceFormatted;
    if (compare) {
      compare.textContent = variant.compareAtPrice > variant.price ? variant.compareAtPriceFormatted : '';
      compare.hidden = variant.compareAtPrice <= variant.price;
    }
    if (availability) {
      availability.dataset.available = String(variant.available);
      availability.textContent = variant.available ? this.dataset.availableLabel : this.dataset.soldOutLabel;
    }
    if (sku) {
      sku.textContent = variant.sku ? `${this.dataset.skuLabel}: ${variant.sku}` : '';
      sku.hidden = !variant.sku;
    }
    if (this.dynamicCheckout) this.dynamicCheckout.hidden = !variant.available;
    this.setPurchaseState(variant.available, variant.available ? this.dataset.addLabel : this.dataset.soldOutLabel);
    if (stickyButton) stickyButton.disabled = !variant.available;
    this.updateQuantityRule(variant.quantityRule);
    if (variant.featuredMediaId) this.selectMedia(String(variant.featuredMediaId), 'auto');

    if (this.dataset.updateUrl === 'true') {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
  }

  setPurchaseState(enabled, label) {
    [this.mainAddButton, this.querySelector('[data-sticky-add]')].forEach((button) => {
      if (!button) return;
      button.disabled = !enabled;
      button.textContent = label;
    });
  }

  updateQuantityRule(rule = {}) {
    if (!this.quantityInput) return;
    this.quantityInput.min = rule.min || 1;
    this.quantityInput.step = rule.increment || 1;
    if (rule.max) this.quantityInput.max = rule.max;
    else this.quantityInput.removeAttribute('max');
    if (Number(this.quantityInput.value) < Number(this.quantityInput.min)) this.quantityInput.value = this.quantityInput.min;
  }

  changeQuantity(action) {
    if (!this.quantityInput) return;
    const step = Number(this.quantityInput.step) || 1;
    const min = Number(this.quantityInput.min) || 1;
    const max = this.quantityInput.max ? Number(this.quantityInput.max) : Infinity;
    const direction = action === 'increase' ? 1 : -1;
    this.quantityInput.value = Math.min(max, Math.max(min, Number(this.quantityInput.value) + (step * direction)));
    this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  selectMedia(mediaId, behavior = 'smooth') {
    const slide = this.querySelector(`[data-media-id="${CSS.escape(mediaId)}"]`);
    if (!slide || !this.mediaStage) return;
    const stageRect = this.mediaStage.getBoundingClientRect();
    const slideRect = slide.getBoundingClientRect();
    const targetLeft = this.mediaStage.scrollLeft + slideRect.left - stageRect.left;
    const scrollBehavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : behavior;
    this.mediaStage.scrollTo({ left: targetLeft, behavior: scrollBehavior });
    this.setActiveThumbnail(mediaId);
  }

  setActiveThumbnail(mediaId) {
    this.querySelectorAll('[data-media-thumbnail]').forEach((button) => {
      button.setAttribute('aria-current', String(button.dataset.mediaTarget === mediaId));
    });
  }

  onMediaScroll() {
    window.clearTimeout(this.mediaScrollTimer);
    this.mediaScrollTimer = window.setTimeout(() => {
      const slides = [...this.querySelectorAll('[data-media-id]')];
      const current = slides.reduce((closest, slide) => (
        Math.abs(slide.offsetLeft - this.mediaStage.scrollLeft) < Math.abs(closest.offsetLeft - this.mediaStage.scrollLeft) ? slide : closest
      ), slides[0]);
      if (current) this.setActiveThumbnail(current.dataset.mediaId);
    }, 80);
  }

  async onSubmit(event) {
    if (event.submitter !== this.mainAddButton) return;
    event.preventDefault();
    if (!this.mainAddButton || this.mainAddButton.disabled || !window.themeCart) return;

    this.mainAddButton.disabled = true;
    this.mainAddButton.setAttribute('aria-busy', 'true');
    this.querySelector('[data-sticky-add]')?.setAttribute('disabled', '');
    if (this.formError) this.formError.hidden = true;

    try {
      await window.themeCart.add(this.productForm);
      this.mainAddButton.textContent = this.dataset.addedLabel;
      const status = document.getElementById('ThemeStatus');
      if (status) status.textContent = this.dataset.addedMessage;
    } catch (error) {
      if (this.formError) {
        this.formError.textContent = error.message || this.dataset.addError;
        this.formError.hidden = false;
      }
    } finally {
      this.mainAddButton.removeAttribute('aria-busy');
      window.setTimeout(() => this.updateVariant(this.currentVariant), 1200);
    }
  }

  setupStickyBar() {
    if (!this.stickyBar) return;
    this.stickyBar.classList.add('is-visible');
  }
}

if (!customElements.get('product-details')) {
  customElements.define('product-details', ProductDetails);
}
